import { useState, useEffect, useMemo, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useAuthStore } from "@/hooks/useAuthStore";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useTeachFranklinData } from "../hooks/useTeachFranklinData";
import {
  useTeachFranklinTagGroups,
  documentTagsToFilterIds,
  filterIdsToDbIds,
} from "../hooks/useTeachFranklinTagGroups";
import { useToast } from "@/hooks/use-toast";
import { eventLogger } from "@/utils/eventLogger";
import {
  syncCategoriesToDocument,
  syncDisciplinesToDocument,
  syncStagesToDocument,
} from "../utils/tagHelpers";
import { GroupSelectorCard } from "./filters/GroupSelectorCard";
import { TagDropdown } from "./filters/TagDropdown";
import { SelectedTagsBar } from "./filters/SelectedTagsBar";
import type { Document } from "@/types/documents";

interface TeachFranklinModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: Document;
  onDocumentUpdate: (updatedDocument: Document) => void;
}

/**
 * TeachFranklinModal - Allow users to correct document tags using the same
 * filter component pattern as the document list filter.
 *
 * Uses GroupSelectorCard, TagDropdown, and SelectedTagsBar for familiar UX.
 */
export function TeachFranklinModal({
  open,
  onOpenChange,
  document,
  onDocumentUpdate,
}: TeachFranklinModalProps) {
  const { categories, disciplines, stages, isLoading: isLoadingOptions } = useTeachFranklinData();
  const { currentOrganization } = useOrganizationContext();
  const authStore = useAuthStore();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [isSaving, setIsSaving] = useState(false);

  // Selection state - using filter ID format for consistency with filter components
  const [selectedFilterIds, setSelectedFilterIds] = useState<string[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Build tag groups from available options (includes category ID mapping)
  const { tagGroups, categoryIdMap } = useTeachFranklinTagGroups({
    categories,
    disciplines,
    stages,
  });

  // Initialize selections from document when modal opens
  useEffect(() => {
    if (open && document) {
      const filterIds = documentTagsToFilterIds(document, categoryIdMap);
      setSelectedFilterIds(filterIds);
      setOpenDropdown(null);
    }
  }, [open, document, categoryIdMap]);

  // Toggle a filter on/off
  const toggleFilter = useCallback((filterId: string) => {
    setSelectedFilterIds((prev) => {
      const isActive = prev.includes(filterId);
      return isActive ? prev.filter((id) => id !== filterId) : [...prev, filterId];
    });
  }, []);

  // Remove a specific filter
  const removeFilter = useCallback((filterId: string) => {
    setSelectedFilterIds((prev) => prev.filter((id) => id !== filterId));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setSelectedFilterIds([]);
  }, []);

  // Build a map of group ID -> tag IDs for efficient lookup
  const tagIdsByGroup = useMemo(() => {
    const map = new Map<string, Set<string>>();
    tagGroups.forEach((group) => {
      const tagIds = new Set(group.tags.map((tag) => tag.id));
      map.set(group.id, tagIds);
    });
    return map;
  }, [tagGroups]);

  // Get selected tags for a specific group
  const getSelectedTagsForGroup = useCallback(
    (groupId: string): string[] => {
      const groupTagIds = tagIdsByGroup.get(groupId);
      if (!groupTagIds) return [];
      return selectedFilterIds.filter((id) => groupTagIds.has(id));
    },
    [selectedFilterIds, tagIdsByGroup]
  );

  // Get count of selected tags for a specific group
  const getSelectedCountForGroup = useCallback(
    (groupId: string): number => {
      return getSelectedTagsForGroup(groupId).length;
    },
    [getSelectedTagsForGroup]
  );

  // Total selected count
  const totalSelectedCount = selectedFilterIds.length;

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Check authentication FIRST before accessing any auth store data
      // This prevents using stale userProfileId if session expired
      if (!authStore.isAuthenticated) {
        toast({
          title: "Session expired",
          description: "Please log in again to save changes.",
          variant: "destructive",
        });
        return;
      }

      // Now safe to access userProfileId - we've confirmed auth is valid
      const userProfileId = authStore.userProfileId;
      if (!userProfileId) {
        toast({
          title: "Profile not loaded",
          description: "Your user profile is still loading. Please try again in a moment.",
          variant: "destructive",
        });
        return;
      }

      const userId = userProfileId;
      const docId = parseInt(document.id);
      const orgId = currentOrganization?.id || "";

      // Capture previous values BEFORE updates for event logging
      const previousCategories =
        document.categories?.map((c) => ({ id: c.id, name: c.name })) || [];
      const previousDisciplines =
        document.design_disciplines?.map((d) => ({ id: d.id, name: d.name })) || [];
      const previousStages = document.design_stages?.map((s) => ({ id: s.id, name: s.name })) || [];

      // Convert filter IDs to database IDs, preserving original category variants
      const originalCategoryIds = document.categories?.map((c) => c.id) || [];
      const { categoryIds, disciplineIds, stageIds } = filterIdsToDbIds(
        selectedFilterIds,
        originalCategoryIds,
        categoryIdMap
      );

      // Store original IDs for potential rollback
      const originalDisciplineIds = document.design_disciplines?.map((d) => d.id) || [];

      // Track which syncs completed for rollback
      let categoriesSynced = false;
      let disciplinesSynced = false;

      // Sync all tag types sequentially with rollback on failure
      // Note: Without server-side transactions, we use compensating actions
      try {
        await syncCategoriesToDocument(docId, categoryIds, userId);
        categoriesSynced = true;

        await syncDisciplinesToDocument(docId, disciplineIds, userId);
        disciplinesSynced = true;

        await syncStagesToDocument(docId, stageIds, userId);
      } catch (syncError) {
        // Attempt to rollback completed operations to maintain consistency
        console.error("TeachFranklinModal: Sync failed, attempting rollback", syncError);
        try {
          if (disciplinesSynced) {
            await syncDisciplinesToDocument(docId, originalDisciplineIds, userId);
          }
          if (categoriesSynced) {
            await syncCategoriesToDocument(docId, originalCategoryIds, userId);
          }
        } catch (rollbackError) {
          console.error("TeachFranklinModal: Rollback failed", rollbackError);
        }
        throw syncError; // Re-throw to trigger error handling
      }

      // Prepare new values for event logging
      const newCategories = categories
        .filter((c) => categoryIds.includes(c.id))
        .map((c) => ({ id: c.id, name: c.category_name }));

      const newDisciplines = disciplines
        .filter((d) => disciplineIds.includes(d.id))
        .map((d) => {
          const row = d as typeof d & { name_en?: string | null };
          return { id: d.id, name: row.name_en?.trim() || d.description?.trim() || d.name };
        });

      const newStages = stages
        .filter((s) => stageIds.includes(s.id))
        .map((s) => {
          const row = s as typeof s & { name_en?: string | null };
          return { id: s.id, name: row.name_en?.trim() || s.name };
        });

      // Log events for changes (fire-and-forget)
      if (orgId) {
        // Check if categories changed
        const categoriesChanged =
          JSON.stringify(previousCategories.map((c) => c.id).sort()) !==
          JSON.stringify(newCategories.map((c) => c.id).sort());

        if (categoriesChanged) {
          eventLogger.log({
            event_type: "document.category.updated",
            entity_type: "document",
            entity_id: document.id,
            org_id: orgId,
            field_name: "categories",
            previous_value: previousCategories,
            new_value: newCategories,
            metadata: { source: "teach_franklin" },
          });
        }

        // Check if disciplines changed
        const disciplinesChanged =
          JSON.stringify(previousDisciplines.map((d) => d.id).sort()) !==
          JSON.stringify(newDisciplines.map((d) => d.id).sort());

        if (disciplinesChanged) {
          eventLogger.log({
            event_type: "document.discipline.updated",
            entity_type: "document",
            entity_id: document.id,
            org_id: orgId,
            field_name: "disciplines",
            previous_value: previousDisciplines,
            new_value: newDisciplines,
            metadata: { source: "teach_franklin" },
          });
        }

        // Check if stages changed
        const stagesChanged =
          JSON.stringify(previousStages.map((s) => s.id).sort()) !==
          JSON.stringify(newStages.map((s) => s.id).sort());

        if (stagesChanged) {
          eventLogger.log({
            event_type: "document.stage.updated",
            entity_type: "document",
            entity_id: document.id,
            org_id: orgId,
            field_name: "stages",
            previous_value: previousStages,
            new_value: newStages,
            metadata: { source: "teach_franklin" },
          });
        }
      }

      // Build updated document from known data (avoids race condition from re-fetching)
      const transformedDoc: Document = {
        ...document,
        categories: newCategories,
        design_disciplines: newDisciplines.map((d) => {
          const disc = disciplines.find((disc) => disc.id === d.id);
          const discRow = disc as typeof disc & { name_en?: string | null };
          return {
            ...d,
            name_en: discRow?.name_en ?? null,
            abbreviation: disc?.abbreviation ?? null,
            description: disc?.description ?? null,
          };
        }),
        design_stages: newStages.map((s) => {
          const stage = stages.find((stage) => stage.id === s.id);
          const stageRow = stage as typeof stage & { name_en?: string | null };
          return {
            ...s,
            name_en: stageRow?.name_en ?? null,
            abbreviation: stage?.abbreviation ?? null,
          };
        }),
      };

      onDocumentUpdate(transformedDoc);

      toast({
        title: t.documents.teachFranklin.successTitle,
        description: t.documents.teachFranklin.successDescription,
      });

      onOpenChange(false);
    } catch (error) {
      console.error("TeachFranklinModal: Error saving tags", error);
      toast({
        title: t.documents.teachFranklin.errorTitle,
        description: t.documents.teachFranklin.errorDescription,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{t.documents.teachFranklin.modalTitle}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t.documents.teachFranklin.modalDescription}{" "}
            <span className="font-medium text-foreground">{document.filename}</span>
          </DialogDescription>
        </DialogHeader>

        {isLoadingOptions ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {/* Group Selector Cards - 3 column grid */}
            <div className="grid grid-cols-3 gap-3">
              {tagGroups.map((group) => (
                <TagDropdown
                  key={group.id}
                  group={group}
                  selectedTags={getSelectedTagsForGroup(group.id)}
                  onTagToggle={toggleFilter}
                  open={openDropdown === group.id}
                  onOpenChange={(isOpen) => setOpenDropdown(isOpen ? group.id : null)}
                  showItemCounts={false}
                  trigger={
                    <GroupSelectorCard
                      group={group}
                      selectedCount={getSelectedCountForGroup(group.id)}
                      isOpen={openDropdown === group.id}
                      showTotalCount={false}
                      disabled={isSaving}
                    />
                  }
                />
              ))}
            </div>

            {/* Selected Tags Bar */}
            {totalSelectedCount > 0 && (
              <SelectedTagsBar
                groups={tagGroups}
                selectedFilters={selectedFilterIds}
                onRemoveFilter={removeFilter}
                onClearAll={clearAllFilters}
                sortByGroup={true}
              />
            )}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-4 border-t pt-4">
          <span className="text-sm text-muted-foreground">
            {t.documents.teachFranklin.tagsSelected.replace("{count}", String(totalSelectedCount))}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              {t.documents.teachFranklin.cancel}
            </Button>
            <Button onClick={handleSave} disabled={isSaving || isLoadingOptions}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t.documents.teachFranklin.saving}
                </>
              ) : (
                t.documents.teachFranklin.saveChanges
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
